import React, { useEffect, useState } from 'react';
import { CheckCircle, X, Calendar, Clock, User, Phone, Hash, Car } from '@phosphor-icons/react';
import { useNotification } from '../context/NotificationContext';
import './NotificationBanner.css';

/**
 * NotificationBanner - компонент для отображения уведомления о бронировании
 * Отображается вверху страницы как sticky баннер
 *
 * Примечание: Для анимации используется CSS transform вместо Tailwind translate
 * утилит из-за проблемы с @property в Shadow DOM.
 */
const NotificationBanner = () => {
  const { notification, hideNotification } = useNotification();
  const [isVisible, setIsVisible] = useState(false);

  // Управление анимацией появления/исчезновения
  useEffect(() => {
    if (notification) {
      // Небольшая задержка для анимации
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [notification]);

  // Если нет уведомления, не рендерим компонент
  if (!notification) {
    return null;
  }

  const { date, time, name, phone, bookingId, carInfo } = notification;

  // Форматирование даты для более читабельного вида
  const formatDate = (dateString) => {
    try {
      const dateObj = new Date(dateString);
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div
      className={`notification-banner ${
        isVisible ? 'notification-banner-visible' : 'notification-banner-hidden'
      }`}
    >
      <div className="notification-banner__inner">
        <div className="notification-banner__row">
          {/* Left side - Success icon and main message */}
          <div className="notification-banner__main">
            <CheckCircle className="notification-banner__icon" />

            <div className="notification-banner__body">
              <h3 className="notification-banner__title">
                {carInfo ? 'Test drive' : 'Service appointment'} booked successfully!
              </h3>

              {/* Booking details grid */}
              <div className="notification-banner__grid">
                {/* Date */}
                <div className="notification-banner__item">
                  <Calendar className="notification-banner__item-icon" />
                  <span className="notification-banner__value">
                    <span >Date:</span> {formatDate(date)}
                  </span>
                </div>

                {/* Time */}
                <div className="notification-banner__item">
                  <Clock className="notification-banner__item-icon" />
                  <span className="notification-banner__value">
                    <span >Time:</span> {time}
                  </span>
                </div>

                {/* Name */}
                <div className="notification-banner__item">
                  <User className="notification-banner__item-icon" />
                  <span className="notification-banner__value">
                    <span >Name:</span> {name}
                  </span>
                </div>

                {/* Phone */}
                <div className="notification-banner__item">
                  <Phone className="notification-banner__item-icon" />
                  <span className="notification-banner__value">
                    <span >Phone:</span> {phone}
                  </span>
                </div>

                {/* Car Info (if available) */}
                {carInfo && (
                  <div className="notification-banner__item">
                    <Car className="notification-banner__item-icon" />
                    <span className="notification-banner__value">
                      <span >Car:</span> {carInfo}
                    </span>
                  </div>
                )}
              </div>

              {/* Confirmation Code - ВЫДЕЛЕННЫЙ БОЛЬШОЙ БЛОК */}
              <div className="notification-banner__code">
                <div className="notification-banner__code-box">
                  <Hash className="notification-banner__icon" />
                  <div className="notification-banner__code-hint">
                    <div className="notification-banner__code-label">
                      Confirmation code
                    </div>
                    <div className="notification-banner__code-value">
                      {bookingId}
                    </div>
                  </div>
                </div>
                <p className="notification-banner__code-hint">
                  Please save this code for your records
                </p>
              </div>

              {/* Additional message */}
              <p className="notification-banner__hint">
                We'll send you a confirmation email shortly. See you soon!
              </p>
            </div>
          </div>

          {/* Right side - Close button */}
          <button
            onClick={hideNotification}
            className="notification-banner__close-btn"
            aria-label="Close notification"
          >
            <X  />
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationBanner;
